/**
 * SortableStopCard - Individual draggable stop card
 *
 * Features:
 * - Drag & drop reordering
 * - Time window display with violation warning
 * - Editable stoppage time per stop
 * - Expanded view with work order details
 * - Quick navigation to Google Maps
 * - Remove stop action
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useRoutePlannerStore } from '../../stores/route-planner-store';
import { useRoutePlannerDrawerStore } from '../../stores/route-planner-drawer-store';
import { Badge } from '../ui';
import { RemoveStopDialog } from './RemoveStopDialog';
import type { RouteStop } from '../../lib/salesforce/types';
import { formatDateTime, formatTijd } from '../../lib/format';
import clsx from 'clsx';

interface SortableStopCardProps {
  stop: RouteStop;
  index: number;
  disabled?: boolean;
}

// ============================================================================
// ICONS
// ============================================================================

const DragHandleIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const NavigateIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const PinOffIcon = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 01-.657-10.657" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.88 4.24A8 8 0 0117.66 16.66" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const AlertIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const OptimizeIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const PackageIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

const StarIcon = () => (
  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const CarIcon = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const PeopleIcon = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 00-3-3.87" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const SidebarIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 4v16" />
  </svg>
);

// ============================================================================
// HELPERS
// ============================================================================

function formatArrivalTime(isoDatetime: string | null): string | null {
  return formatTijd(isoDatetime) || null;
}

function formatAgreedTimeWindow(datumTijdVanaf: string | null, datumTijdTot: string | null): string | null {
  if (!datumTijdVanaf && !datumTijdTot) return null;
  const parts = [formatTijd(datumTijdVanaf), formatTijd(datumTijdTot)].filter(Boolean);
  return parts.length === 2 ? parts.join(' - ') : parts[0] || null;
}

/**
 * Determine time window violation type: 'early', 'late', or null
 * @param arrivalTime - ISO datetime of estimated arrival
 * @param datumTijdVanaf - ISO datetime of agreed start time
 * @param datumTijdTot - ISO datetime of agreed end time
 */
function getTimeWindowViolationType(
  arrivalTime: string | null,
  datumTijdVanaf: string | null,
  datumTijdTot: string | null
): 'early' | 'late' | null {
  if (!arrivalTime) return null;
  if (!datumTijdVanaf && !datumTijdTot) return null;

  try {
    const arrival = new Date(arrivalTime);

    if (datumTijdVanaf) {
      const start = new Date(datumTijdVanaf);
      if (arrival < start) return 'early';
    }

    if (datumTijdTot) {
      const end = new Date(datumTijdTot);
      if (arrival > end) return 'late';
    }

    return null; // Within window
  } catch {
    return null;
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export function SortableStopCard({ stop, index, disabled }: SortableStopCardProps) {
  const {
    stops,
    route,
    selectedStopId,
    setSelectedStop,
    selectedStopIds,
    toggleStopSelection,
    setStoppageTimeOverride,
    updateStopTimeWindow,
    removeStop,
    isRemovingStop,
    openNietGeluktDialog,
    openTochGeslaagdDialog,
    isLocked,
    isOptimizing,
    optimizeFromStop,
    regeocodeStop,
    isRegeocoding,
    setStopBijrijderNodig,
  } = useRoutePlannerStore();

  const openDrawer = useRoutePlannerDrawerStore((s) => s.open);

  const isSelected = selectedStopId === stop.id;
  const isMultiSelected = selectedStopIds.has(stop.id);
  const [localStoppageTime, setLocalStoppageTime] = useState(stop.stoppageTime);
  const [localTimeFrom, setLocalTimeFrom] = useState(stop.timeFrom || '');
  const [localTimeTo, setLocalTimeTo] = useState(stop.timeTo || '');
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);

  // Sync local stoppage time when stop data changes (e.g., after recalculation)
  useEffect(() => {
    setLocalStoppageTime(stop.stoppageTime);
  }, [stop.stoppageTime]);

  // Sync local time window when stop data changes
  useEffect(() => {
    setLocalTimeFrom(stop.timeFrom || '');
    setLocalTimeTo(stop.timeTo || '');
  }, [stop.timeFrom, stop.timeTo]);

  // Check if this stop can be manipulated (drag/remove)
  // On locked routes, only added-after-lock stops can be manipulated
  const canManipulate = !isLocked || stop.isAddedAfterLock;

  // "Niet gelukt vandaag" is een afhandel-actie (completion-state), niet
  // structureel — toegestaan op vergrendelde routes. Alleen voor stops die nog
  // niet verwerkt/overgeslagen zijn en een gekoppelde werkorder hebben.
  const canMarkNietGelukt =
    stop.status !== 'Verwerkt' && !stop.overgeslagen && !!stop.werkOrderId;

  // "Toch geslaagd" is de inverse: een overgeslagen stop (niet thuis) alsnog als
  // geslaagd markeren — chauffeur ging toch langs en heeft opgehaald/bezorgd.
  // Zelfde knop voor een stop die niemand afmeldde terwijl de route al is
  // afgerond (12-09: twee bezorgstops bleven 'Ingepland', kleden zes weken
  // 'In bus'); naast "Niet gelukt" is dit de andere helft van dat besluit.
  const routeAfgerond = route?.status === 'Afgerond';
  const nooitAfgemeld = routeAfgerond && stop.status !== 'Verwerkt' && !stop.overgeslagen;
  const canMarkTochGeslaagd = (stop.overgeslagen || nooitAfgemeld) && !!stop.werkOrderId;

  // Debug logging for locked route manipulation
  if (isLocked) {
    console.log(`Stop ${stop.routeSequence}: isAddedAfterLock=${stop.isAddedAfterLock}, canManipulate=${canManipulate}`);
  }

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stop.id, disabled: disabled || !canManipulate });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Build Google Maps navigation link
  const googleNavigateLink = stop.fullAddress
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stop.fullAddress)}`
    : null;

  // Bijrijder-indicator: afgeleid uit de Bijrijdertoeslag-regel of handmatig gezet
  const bijrijderBedrag = stop.bijrijderToeslagBedrag ?? 0;
  const bijrijderBedragFmt = bijrijderBedrag.toLocaleString('nl-NL', { maximumFractionDigits: 2 });
  const bijrijderTooltip =
    stop.bijrijderBron === 'toeslag'
      ? `Bijrijdertoeslag: ${stop.bijrijderToeslagAantal ?? ''}× · €${bijrijderBedragFmt}${bijrijderBedrag === 0 ? ' (niet gefactureerd)' : ''}`
      : stop.bijrijderBron === 'handmatig'
        ? 'Handmatig gemarkeerd: bijrijder nodig'
        : 'Bijrijder nodig';

  const arrivalTimeFormatted = formatArrivalTime(stop.arrivalTime);
  const hasTimeWindow = stop.timeFrom && stop.timeTo;
  const isTimeWindowViolation = !stop.isWithinTimeWindow && hasTimeWindow;
  const agreedTimeWindow = formatAgreedTimeWindow(stop.datumTijdVanaf, stop.datumTijdTot);
  const timeWindowViolationType = getTimeWindowViolationType(stop.arrivalTime, stop.datumTijdVanaf, stop.datumTijdTot);
  const hasAgreedTimeViolation = timeWindowViolationType !== null;

  // Handle stoppage time change
  const handleStoppageTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= 120) {
      setLocalStoppageTime(value);
    }
  }, []);

  const handleStoppageTimeBlur = useCallback(() => {
    if (localStoppageTime !== stop.stoppageTime) {
      setStoppageTimeOverride(stop.id, localStoppageTime);
    }
  }, [localStoppageTime, stop.stoppageTime, stop.id, setStoppageTimeOverride]);

  const handleTimeWindowBlur = useCallback(() => {
    const newFrom = localTimeFrom || null;
    const newTo = localTimeTo || null;
    if (newFrom !== (stop.timeFrom || null) || newTo !== (stop.timeTo || null)) {
      updateStopTimeWindow(stop.id, newFrom, newTo);
    }
  }, [localTimeFrom, localTimeTo, stop.timeFrom, stop.timeTo, stop.id, updateStopTimeWindow]);

  const handleRemoveClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowRemoveDialog(true);
  }, []);

  const handleRemoveConfirm = useCallback((newStatus: string) => {
    removeStop(stop.id, newStatus);
    setShowRemoveDialog(false);
  }, [removeStop, stop.id]);

  // A stop is a duplicate when the same werk order appears again on this route
  // with the same direction (Ophalen/Terugbrengen). Only then is it safe to
  // remove it without touching the werk order — the other stop keeps it scheduled.
  const isDuplicateStop = useMemo(() => {
    if (!stop.werkOrderId) return false;
    return stops.some(
      (s) =>
        s.id !== stop.id &&
        s.werkOrderId === stop.werkOrderId &&
        s.stopType === stop.stopType,
    );
  }, [stops, stop.id, stop.werkOrderId, stop.stopType]);

  const handleRemoveCancel = useCallback(() => {
    setShowRemoveDialog(false);
  }, []);

  const handleOptimizeFromHere = useCallback(() => {
    // Use 1-based index for the route sequence
    // "Optimaliseer vanaf hier" means: keep this stop and all before it fixed,
    // optimize everything AFTER this stop
    // So for stop at sequence N, we call optimizeFromStop(N + 1)
    const sequence = stop.routeSequence ?? index + 1;
    optimizeFromStop(sequence + 1);
  }, [optimizeFromStop, stop.routeSequence, index]);

  const handleRegeocode = useCallback(() => {
    regeocodeStop(stop.id);
  }, [regeocodeStop, stop.id]);

  // Copy phone number to clipboard
  const [phoneCopied, setPhoneCopied] = useState(false);
  const handleCopyPhone = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (stop.contactPhone) {
      navigator.clipboard.writeText(stop.contactPhone);
      setPhoneCopied(true);
      setTimeout(() => setPhoneCopied(false), 2000);
    }
  }, [stop.contactPhone]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        'bg-white rounded-lg shadow-sm border transition-all duration-150',
        isDragging && 'shadow-lg opacity-90 z-50 ring-2 ring-cinco-primary',
        isSelected ? 'border-cinco-primary ring-2 ring-cinco-primary/20' : 'border-gray-200',
        !disabled && canManipulate && 'hover:shadow-md',
        stop.isCompleted && 'opacity-60',
        // Highlight new stops with orange left border
        stop.isAddedAfterLock && 'border-l-4 border-l-orange-400',
        // Multi-select highlight (does not override the expanded selection ring)
        isMultiSelected && !isSelected && 'ring-2 ring-blue-400'
      )}
    >
      <div className="flex">
        {/* Multi-select checkbox */}
        {canManipulate && (
          <div className="flex items-center justify-center w-8 border-r border-gray-100">
            <input
              type="checkbox"
              checked={isMultiSelected}
              onChange={(e) => {
                e.stopPropagation();
                toggleStopSelection(stop.id);
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
              title={isMultiSelected ? 'Selectie ongedaan maken' : 'Selecteer stop voor verplaatsen'}
            />
          </div>
        )}

        {/* Drag handle */}
        {!disabled && canManipulate && (
          <div
            {...attributes}
            {...listeners}
            className="flex items-center justify-center w-8 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-l-lg transition-colors"
          >
            <DragHandleIcon />
          </div>
        )}

        {/* Sequence number */}
        <div className="flex items-center justify-center w-10">
          <div
            className={clsx(
              'w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold',
              stop.isCompleted
                ? 'bg-green-500 text-white'
                : stop.handmatigInplannen
                  ? 'bg-purple-500 text-white'
                  : isTimeWindowViolation
                    ? 'bg-amber-500 text-white'
                    : stop.stopType === 'Ophalen'
                      ? 'bg-blue-500 text-white'
                      : 'bg-emerald-500 text-white'
            )}
          >
            {stop.isCompleted ? <CheckIcon /> : (stop.routeSequence ?? index + 1)}
          </div>
        </div>

        {/* Main content */}
        <button
          onClick={() => setSelectedStop(isSelected ? null : stop.id)}
          className="flex-1 p-3 text-left min-w-0"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              {/* Customer/Account name */}
              <h4 className="font-medium text-gray-900 truncate flex items-center gap-1.5">
                {stop.accountId ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDrawer({ type: 'klant', id: stop.accountId!, klantType: 'account' });
                    }}
                    className="truncate text-left hover:text-indigo-600 hover:underline"
                    title="Open klant"
                  >
                    {stop.accountName || stop.contactName || 'Onbekende klant'}
                  </button>
                ) : (
                  <span className="truncate">{stop.accountName || stop.contactName || 'Onbekende klant'}</span>
                )}
                {stop.werkOrderId && stop.werkOrderName && (
                  <span
                    className="text-[10px] font-mono text-gray-500 flex-shrink-0"
                    title={stop.werkOrderName}
                  >
                    {stop.werkOrderName}
                  </span>
                )}
              </h4>

              {/* Address */}
              <p className="text-sm text-gray-500 mt-0.5">
                {stop.street}, {stop.postalCode} {stop.city}
              </p>

              {/* Geocode missing badge */}
              {(stop.latitude === null || stop.longitude === null) && (
                <span
                  className={clsx(
                    'inline-flex items-center gap-1 px-1.5 py-0.5 mt-1 text-xs rounded border',
                    stop.geocodeStatus === 'Failed'
                      ? 'bg-red-50 text-red-700 border-red-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'
                  )}
                  title={stop.geocodeError
                    ? `Geocoding mislukt: ${stop.geocodeError}${stop.geocodeLastAttempt ? ` (${formatDateTime(stop.geocodeLastAttempt)})` : ''}`
                    : 'Nog niet gegeocodeerd — adres staat niet op de kaart'}
                >
                  <PinOffIcon />
                  {stop.geocodeStatus === 'Failed' ? 'Geocoding mislukt' : 'Geen locatie'}
                </span>
              )}

              {/* Meta info row */}
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2 text-xs">
                {/* Agreed time window (afgesproken tijden) */}
                {agreedTimeWindow && (
                  <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700" title="Afgesproken tijdslot">
                    📅 {agreedTimeWindow}
                  </span>
                )}

                {/* Time window */}
                {hasTimeWindow && (
                  <span className={clsx(
                    'px-1.5 py-0.5 rounded text-xs',
                    isTimeWindowViolation
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-100 text-gray-600'
                  )}>
                    {stop.timeFrom} - {stop.timeTo}
                  </span>
                )}

                {/* Distance and travel time to next stop */}
                {(stop.distanceKm || stop.hourAndMinutes) && (
                  <span className="flex items-center gap-1 text-gray-500" title="Afstand en rijtijd naar volgende stop">
                    <CarIcon />
                    {stop.distanceKm}
                    {stop.hourAndMinutes && (
                      <span className="text-gray-400">({stop.hourAndMinutes.replace(' hr ', 'u ').replace(' min', 'm')})</span>
                    )}
                  </span>
                )}

                {/* m² */}
                {stop.totaalM2 !== null && stop.totaalM2 > 0 && (
                  <span className="flex items-center gap-1 text-gray-500">
                    <PackageIcon />
                    {stop.totaalM2.toFixed(1)} m²
                  </span>
                )}

                {/* Stoppage time */}
                <span className="text-gray-400">
                  +{stop.stoppageTime} min
                </span>
              </div>

              {/* Opmerkingen — altijd zichtbaar */}
              {stop.stopType === 'Ophalen' && stop.opmerkingenOphalen && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-blue-800 text-xs">
                  <strong>Ophalen:</strong> {stop.opmerkingenOphalen}
                </div>
              )}
              {stop.stopType === 'Terugbrengen' && stop.opmerkingenAfleveren && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-green-800 text-xs">
                  <strong>Afleveren:</strong> {stop.opmerkingenAfleveren}
                </div>
              )}
              {stop.opmerkingenPlanning && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-xs">
                  <strong>Planning:</strong> {stop.opmerkingenPlanning}
                </div>
              )}

              {/* Lading — welke kleden hier opgehaald/afgeleverd worden.
                  Bij reparateur-stops: ophalen = gerepareerd, afleveren = ter reparatie. */}
              {((stop.cargoOphalen?.length ?? 0) > 0 || (stop.cargoAfleveren?.length ?? 0) > 0) && (
                <div className="mt-2 space-y-1.5">
                  {(stop.cargoOphalen?.length ?? 0) > 0 && (
                    <div className="rounded border border-blue-200 bg-blue-50 p-2">
                      <div className="text-[11px] font-semibold text-blue-800">
                        Ophalen ({stop.cargoOphalen?.length})
                      </div>
                      <ul className="mt-1 space-y-0.5">
                        {(stop.cargoOphalen ?? []).map((c) => (
                          <li key={c.wiId} className="flex items-baseline gap-1.5 text-[11px] text-blue-900">
                            <span className="font-mono text-blue-700">{c.naam}</span>
                            {c.afmetingen && <span className="text-blue-700/80">{c.afmetingen}</span>}
                            {c.isReparatie && (
                              <span className="shrink-0 rounded bg-amber-100 px-1 py-px text-[10px] font-semibold text-amber-800">
                                Reparatie
                              </span>
                            )}
                            {(stop.isReparateurStop || c.isReparatie) && c.klantNaam && (
                              <span className="truncate text-blue-600/70">· {c.klantNaam}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(stop.cargoAfleveren?.length ?? 0) > 0 && (
                    <div className="rounded border border-green-200 bg-green-50 p-2">
                      <div className="text-[11px] font-semibold text-green-800">
                        Afleveren ({stop.cargoAfleveren?.length})
                      </div>
                      <ul className="mt-1 space-y-0.5">
                        {(stop.cargoAfleveren ?? []).map((c) => (
                          <li key={c.wiId} className="flex items-baseline gap-1.5 text-[11px] text-green-900">
                            <span className="font-mono text-green-700">{c.naam}</span>
                            {c.afmetingen && <span className="text-green-700/80">{c.afmetingen}</span>}
                            {c.isReparatie && (
                              <span className="shrink-0 rounded bg-amber-100 px-1 py-px text-[10px] font-semibold text-amber-800">
                                Reparatie
                              </span>
                            )}
                            {(stop.isReparateurStop || c.isReparatie) && c.klantNaam && (
                              <span className="truncate text-green-600/70">· {c.klantNaam}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right side badges and icons */}
            <div className="flex flex-col items-end gap-2 shrink-0">
              {/* ETA - prominent display with time window violation indicator */}
              {arrivalTimeFormatted && (
                <div className="flex flex-col items-end gap-1">
                  <div className={clsx(
                    'flex items-center gap-1 px-2 py-1 rounded-md font-medium text-sm',
                    hasAgreedTimeViolation
                      ? timeWindowViolationType === 'early'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-amber-100 text-amber-700'
                      : isTimeWindowViolation
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-green-100 text-green-700'
                  )} title="Berekende aankomsttijd (ETA)">
                    <ClockIcon />
                    {arrivalTimeFormatted}
                    {(hasAgreedTimeViolation || isTimeWindowViolation) && <AlertIcon />}
                  </div>
                  {/* Show violation message */}
                  {hasAgreedTimeViolation && (
                    <span className={clsx(
                      'text-xs font-semibold px-1.5 py-0.5 rounded',
                      timeWindowViolationType === 'early'
                        ? 'bg-blue-50 text-blue-600'
                        : 'bg-amber-50 text-amber-600'
                    )}>
                      {timeWindowViolationType === 'early' ? '⏰ Te vroeg' : '⚠️ Te laat'}
                    </span>
                  )}
                </div>
              )}

              <Badge
                variant={stop.stopType === 'Ophalen' ? 'primary' : 'success'}
                size="sm"
              >
                {stop.stopType}
              </Badge>

              {/* Klacht-herbehandeling — rood, zelfde signaalkleur als de
                  banner in de field-app en de client-app. De reden staat in de
                  tooltip zodat de kaart compact blijft. */}
              {stop.isKlacht && (
                <span
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700"
                  title={
                    stop.klachtReden
                      ? `Herbehandeling na klacht — ${stop.klachtReden}`
                      : 'Herbehandeling na klacht'
                  }
                >
                  Klacht
                </span>
              )}

              {/* Handmatig inplannen — buiten het automatische routesysteem */}
              {stop.handmatigInplannen && (
                <span
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700"
                  title="Buiten routesysteem — plan deze stop met de hand in"
                >
                  Handmatig inplannen
                </span>
              )}

              {/* Bijrijder nodig — afgeleid uit de Bijrijdertoeslag of handmatig gezet */}
              {stop.bijrijderNodig && (
                <span
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700"
                  title={bijrijderTooltip}
                >
                  <PeopleIcon /> Bijrijder
                </span>
              )}

              {/* New stop indicator for stops added after lock */}
              {stop.isAddedAfterLock && (
                <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-700 animate-pulse" title="Toegevoegd na vergrendeling">
                  <StarIcon /> NIEUW
                </span>
              )}

              {/* Stop-inpasser — waar de automaat deze na-lock-stop heeft gezet.
                  Staat naast NIEUW: de badge zegt "nieuw", dit zegt "en hij
                  staat niet meer achteraan". */}
              {stop.isAddedAfterLock && stop.ingepastOp && (
                <span
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200"
                  title={
                    `Automatisch ingepast ${stop.ingepastNaSequence ? `na stop ${stop.ingepastNaSequence}` : 'vooraan'}` +
                    (stop.ingepastOmwegKm != null ? ` — omweg +${stop.ingepastOmwegKm} km` : '') +
                    (stop.ingepastBespaardKm != null
                      ? `, ${stop.ingepastBespaardKm} km bespaard t.o.v. achteraan`
                      : '') +
                    '. Tijden en tijdvensters zijn bewust niet herrekend: de klanten hebben hun tijdmail al.'
                  }
                >
                  ingepast
                  {stop.ingepastBespaardKm != null && stop.ingepastBespaardKm > 0
                    ? ` · −${Math.round(stop.ingepastBespaardKm)} km`
                    : ''}
                </span>
              )}

              {/* Overgeslagen — niet gelukt (office of chauffeur) */}
              {stop.overgeslagen && (
                <span
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700"
                  title="Deze stop is gemarkeerd als niet gelukt — klant kan zelf herplannen"
                >
                  <AlertIcon /> Niet gelukt
                </span>
              )}

              <div className="flex items-center gap-1">
                {/* Phone copy button */}
                {stop.contactPhone && (
                  <button
                    onClick={handleCopyPhone}
                    className={clsx(
                      'p-1.5 rounded transition-colors',
                      phoneCopied
                        ? 'text-green-600 bg-green-50'
                        : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                    )}
                    title={phoneCopied ? 'Gekopieerd!' : `Kopieer ${stop.contactPhone}`}
                  >
                    <PhoneIcon />
                  </button>
                )}

                {/* Sidebar opener — open WO in drawer */}
                {stop.werkOrderId && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDrawer({ type: 'order', id: stop.werkOrderId! });
                    }}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                    title="Open werkorder in zijbalk"
                  >
                    <SidebarIcon />
                  </button>
                )}

                {/* Google Maps navigation */}
                {googleNavigateLink && (
                  <a
                    href={googleNavigateLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Navigeer naar adres"
                  >
                    <NavigateIcon />
                  </a>
                )}
                {canManipulate && (
                  <button
                    onClick={handleRemoveClick}
                    disabled={isRemovingStop}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                    title="Verwijder stop"
                  >
                    <TrashIcon />
                  </button>
                )}
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Expanded details */}
      {isSelected && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 space-y-4 animate-fadeIn">
          {/* Action buttons */}
          <div className="flex gap-2">
            {canManipulate && (
              <button
                onClick={handleOptimizeFromHere}
                disabled={isOptimizing || stops.length <= (stop.routeSequence ?? index + 1)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Behoud deze stop op huidige positie, optimaliseer de rest van de route"
              >
                <OptimizeIcon />
                {isOptimizing ? 'Bezig...' : 'Optimaliseer rest'}
              </button>
            )}
            <button
              onClick={handleRegeocode}
              disabled={isRegeocoding}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Locatie opnieuw bepalen op basis van het adres"
            >
              <NavigateIcon />
              {isRegeocoding
                ? 'Bezig...'
                : (stop.latitude === null || stop.longitude === null)
                  ? 'Geocoderen'
                  : 'Opnieuw geocoderen'}
            </button>
            {canMarkNietGelukt && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openNietGeluktDialog([stop.id]);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-md transition-colors"
                title="Niet gelukt vandaag — werkorder terug naar plannen, klant herplant zelf"
              >
                <AlertIcon />
                Niet gelukt
              </button>
            )}
            {canMarkTochGeslaagd && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openTochGeslaagdDialog([stop.id]);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-md transition-colors"
                title={
                  nooitAfgemeld
                    ? 'Stop is nooit afgemeld op deze afgeronde route — alsnog als geslaagd afmelden; werkorder naar Opgehaald/Teruggebracht'
                    : 'Toch geslaagd — chauffeur is alsnog langs geweest; werkorder naar Opgehaald/Teruggebracht'
                }
              >
                <CheckIcon />
                {nooitAfgemeld
                  ? stop.stopType === 'Ophalen' ? 'Alsnog opgehaald' : 'Alsnog bezorgd'
                  : stop.stopType === 'Ophalen' ? 'Toch opgehaald' : 'Toch bezorgd'}
              </button>
            )}
          </div>

          {/* Stoppage time editor */}
          {!isLocked && (
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600">Stoptijd:</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={120}
                  value={localStoppageTime}
                  onChange={handleStoppageTimeChange}
                  onBlur={handleStoppageTimeBlur}
                  className="w-16 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-cinco-primary/20 focus:border-cinco-primary"
                />
                <span className="text-sm text-gray-500">minuten</span>
              </div>
            </div>
          )}

          {/* Time window editor */}
          {!isLocked && (
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600">Tijdvenster:</label>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={localTimeFrom}
                  onChange={(e) => setLocalTimeFrom(e.target.value)}
                  onBlur={handleTimeWindowBlur}
                  className="px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-cinco-primary/20 focus:border-cinco-primary"
                />
                <span className="text-sm text-gray-400">-</span>
                <input
                  type="time"
                  value={localTimeTo}
                  onChange={(e) => setLocalTimeTo(e.target.value)}
                  onBlur={handleTimeWindowBlur}
                  className="px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-cinco-primary/20 focus:border-cinco-primary"
                />
              </div>
            </div>
          )}

          {/* Bijrijder nodig — handmatige override. Toeslag-gedreven is read-only (volgt uit de werkorder). */}
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600">Bijrijder:</label>
            <label
              className={clsx(
                'flex items-center gap-2 text-sm',
                stop.bijrijderBron === 'toeslag' || isLocked
                  ? 'text-gray-400'
                  : 'text-gray-700 cursor-pointer'
              )}
              title={
                stop.bijrijderBron === 'toeslag'
                  ? `Volgt uit de Bijrijdertoeslag op de werkorder (${bijrijderTooltip})`
                  : isLocked
                    ? 'Route is vergrendeld'
                    : 'Markeer deze stop als bijrijder nodig'
              }
            >
              <input
                type="checkbox"
                checked={!!stop.bijrijderNodig}
                disabled={stop.bijrijderBron === 'toeslag' || isLocked}
                onChange={(e) => setStopBijrijderNodig(stop.id, e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-60"
              />
              <span>
                Bijrijder nodig
                {stop.bijrijderBron === 'toeslag' && (
                  <span className="text-gray-400">
                    {' '}· volgt uit Bijrijdertoeslag ({stop.bijrijderToeslagAantal}× · €{bijrijderBedragFmt})
                  </span>
                )}
              </span>
            </label>
          </div>

          {/* Werk order info */}
          {stop.werkOrderId && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide">Werk Order</p>
                <p className="text-gray-900 font-medium">{stop.werkOrderName}</p>
              </div>
              {stop.status && (
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Status</p>
                  <p className="text-gray-900">{stop.status}</p>
                </div>
              )}
            </div>
          )}

          {/* Times */}
          {(stop.arrivalTime || stop.departureTime) && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              {stop.arrivalTime && (
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Aankomst</p>
                  <p className="text-gray-900">{formatArrivalTime(stop.arrivalTime)}</p>
                </div>
              )}
              {stop.departureTime && (
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Vertrek</p>
                  <p className="text-gray-900">{formatArrivalTime(stop.departureTime)}</p>
                </div>
              )}
            </div>
          )}

          {/* Address details */}
          <div className="text-sm">
            <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Adres</p>
            <p className="text-gray-900">
              {stop.street}<br />
              {stop.postalCode} {stop.city}
            </p>
          </div>

          {/* Items */}
          {(stop.totaalM2 || stop.itemCount) && (
            <div className="flex items-center gap-4 text-sm">
              {stop.totaalM2 !== null && stop.totaalM2 > 0 && (
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Oppervlakte</p>
                  <p className="text-gray-900 font-medium">{stop.totaalM2.toFixed(1)} m²</p>
                </div>
              )}
              {stop.itemCount !== null && stop.itemCount > 0 && (
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Items</p>
                  <p className="text-gray-900 font-medium">{stop.itemCount}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Remove Stop Dialog */}
      <RemoveStopDialog
        stop={stop}
        isOpen={showRemoveDialog}
        onClose={handleRemoveCancel}
        onConfirm={handleRemoveConfirm}
        isRemoving={isRemovingStop}
        isDuplicate={isDuplicateStop}
      />
    </div>
  );
}
